import { useEffect, useRef, useState } from 'react';
import type { GeoJSONSource, Map as MapLibreMap, MapMouseEvent, StyleSpecification } from 'maplibre-gl';
import type { MapGap } from '@popcart/core/v1';
import { tokens } from '@popcart/tokens';
// The shared style lives in @popcart/ui, whose index pulls React Native; import the style file alone.
import { popcartTrackingStyle, styleFromApi } from '../../../../../packages/ui/src/map/style';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from '../zones/lib.js';
import { addKitIcons } from '../../ui/mapIcons';
import { gapsGeoJSON, pointsBounds } from './lib.js';
import 'maplibre-gl/dist/maplibre-gl.css';

const c = tokens.colour;

/**
 * Every map gap's centre on the PopCart map: Pop Yellow dots with an Ink edge, bigger for more
 * deliveries; the selected one is Cart Blue and the map flies to it. Clicking a dot selects it.
 * MapLibre loads on demand, so the page (and its tests) never pull WebGL in up front.
 */
export function GapsMap({ gaps, selectedId, onSelect }: { gaps: MapGap[]; selectedId: string | null; onSelect(id: string): void }) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const live = useRef({ onSelect });
  live.current = { onSelect };

  useEffect(() => {
    let cancelled = false;
    let map: MapLibreMap | null = null;
    void Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')])
      .then(([{ Map, NavigationControl, setWorkerUrl }, { default: workerUrl }]) => {
        if (cancelled || !box.current) return;
        setWorkerUrl(workerUrl);
        map = new Map({ container: box.current, style: styleFromApi(popcartTrackingStyle, window.location.origin) as unknown as StyleSpecification, center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, attributionControl: { compact: true } });
        addKitIcons(map);
        map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          if (!map) return;
          map.addSource('gaps', { type: 'geojson', data: gapsGeoJSON([]) });
          map.addLayer({
            id: 'gaps',
            type: 'circle',
            source: 'gaps',
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['get', 'deliveries'], 3, 7, 20, 14],
              'circle-color': ['case', ['==', ['get', 'id'], ''], c.core.ultra, c.core.volt],
              'circle-stroke-color': c.core.ink,
              'circle-stroke-width': 3,
            },
          });
          setReady(true);
        });
        map.on('click', (e: MapMouseEvent) => {
          const id = map?.queryRenderedFeatures(e.point, { layers: ['gaps'] })[0]?.properties?.id;
          if (typeof id === 'string') live.current.onSelect(id);
        });
        map.on('error', () => undefined);
        mapRef.current = map;
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
  }, []);

  // The dots, and the view around all of them whenever the set changes.
  const ids = gaps.map((g) => g.id).join('|');
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource('gaps') as GeoJSONSource | undefined)?.setData(gapsGeoJSON(gaps) as never);
    const b = pointsBounds(gaps.map((g) => g.center));
    if (b) map.fitBounds(b, { padding: 40, duration: 0, maxZoom: 16 });
  }, [ready, ids]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    map.setPaintProperty('gaps', 'circle-color', ['case', ['==', ['get', 'id'], selectedId ?? ''], c.core.ultra, c.core.volt]);
    const g = selectedId ? gaps.find((x) => x.id === selectedId) : null;
    if (g) map.flyTo({ center: [g.center.lng, g.center.lat], zoom: 17 });
  }, [ready, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mh-map-wrap">
      <div ref={box} className="mh-map" role="img" aria-label={`Map of ${gaps.length} map gap${gaps.length === 1 ? '' : 's'}`} />
      {failed ? (
        <p className="mh-map-msg pill st-warning err-pill" role="status" style={{ whiteSpace: 'normal' }}>
          The map didn't load. Every gap is in the list.
        </p>
      ) : !ready ? (
        <p className="mh-map-msg t-monocap muted" role="status">
          Loading the map…
        </p>
      ) : null}
    </div>
  );
}
